import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  OAuthProvider,
  FacebookAuthProvider,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  User
} from 'firebase/auth';

import { getFirebaseApp } from '../services/firebase';
import { httpClient } from './http';
import { API_ENDPOINTS } from '../config/api';
import { headerService } from './headers';
import { storageService } from './storage';

// Inicializar Firebase Auth
const auth = getAuth(getFirebaseApp());

// Proveedores de autenticación
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');
const facebookProvider = new FacebookAuthProvider();

// Servicio de autenticación de Firebase
export const authService = {
  auth,

  // Iniciar sesión con Google
  async signInWithGoogle(): Promise<User | null> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error: any) {
      console.error("Error al iniciar sesión con Google:", error.message);
      throw error;
    }
  },

  // Iniciar sesión con Apple
  async signInWithApple(): Promise<User | null> {
    try {
      const result = await signInWithPopup(auth, appleProvider);
      return result.user;
    } catch (error: any) {
      console.error("Error al iniciar sesión con Apple:", error.message);
      throw error;
    }
  },

  // Iniciar sesión con Facebook
  async signInWithFacebook(): Promise<User | null> {
    try {
      const result = await signInWithPopup(auth, facebookProvider);
      return result.user;
    } catch (error: any) {
      console.error("Error al iniciar sesión con Facebook:", error.message);
      throw error;
    }
  },

  // Iniciar sesión con email y password
  async signIn(email: string, password: string): Promise<boolean> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return !!result.user;
    } catch (error: any) {
      console.error("Error al iniciar sesión con email/password:", error.message);
      return false;
    }
  },

  // Cerrar sesión
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
      console.log('✅ Sesión de Firebase cerrada');
    } catch (error) {
      console.error('❌ Error cerrando sesión de Firebase:', error);
      throw error;
    }
  },

  // Listener de cambios de autenticación
  onAuthChange(callback: (user: User | null) => void) {
    return auth.onAuthStateChanged(callback);
  }
};

/**
 * Servicio de autenticación que maneja login y signup automático
 */
export const authApiService = {
    /**
   * Función helper optimizada para manejar el proceso completo de login + signup
   */
async handleLoginWithSignup(
    providerName: string,
    signInFunction: () => Promise<User | null>,
    role?: string | null
  ): Promise<User | null> {
    try {
      console.log(`🔄 Iniciando login con ${providerName}...`);
      
      // 1. Login con el proveedor
      const firebaseUser = await signInFunction();
      if (!firebaseUser) {
        throw new Error(`No se pudo obtener información del usuario de ${providerName}`);
      }
      console.log(`✅ Login con ${providerName} exitoso:`, firebaseUser.email);
      
      // 2. Obtener token de Firebase
      const idToken = await firebaseUser.getIdToken();
      
      // 3. Crear usuario en el backend (signup automático)
      console.log('🔄 Ejecutando signup en el backend...');
      await this.createUserInBackend(firebaseUser, idToken, role);
      
      // 4. Refrescar token una sola vez para obtener claims actualizados
      console.log('🔄 Obteniendo claims actualizados...');
      await firebaseUser.getIdToken(true);
      const tokenResult = await firebaseUser.getIdTokenResult();
      const customClaims = tokenResult.claims;
      console.log('✅ Claims actualizados:', customClaims);
      
      // 5. Actualizar token en headers
      const newToken = await firebaseUser.getIdToken();
      headerService.setAuthToken(newToken);
      
      // 6. Actualizar storage si hay claims
      if (customClaims.otsUser) {
        // Asegurar que el usuario tenga el id (uid de Firebase)
        const userDataWithId = {
          ...customClaims.otsUser,
          id: firebaseUser.uid
        };
        await storageService.saveUser(userDataWithId);
        console.log('💾 Storage actualizado con claims del backend');
      }
      
      return firebaseUser;
    } catch (error: any) {
      console.error(`❌ Error en login con ${providerName}:`, error);
      throw error;
    }
  },

  /**
   * Inicia sesión con Google y crea el usuario en el backend si no existe
   */
  async signInWithGoogleAndSignUp(role?: string | null) {
    return this.handleLoginWithSignup('Google', () => authService.signInWithGoogle(), role);
  },

  /**
   * Inicia sesión con Apple y crea el usuario en el backend si no existe
   */
  async signInWithAppleAndSignUp() {
    return this.handleLoginWithSignup('Apple', () => authService.signInWithApple());
  },

  /**
   * Inicia sesión con Facebook y crea el usuario en el backend si no existe
   */
  async signInWithFacebookAndSignUp() {
    return this.handleLoginWithSignup('Facebook', () => authService.signInWithFacebook());
  },

  /**
   * Crea o verifica el usuario en el backend
   */
  async createUserInBackend(firebaseUser: User, idToken: string, role?: string | null) {
    try {
      console.log('🔄 Creando/verificando usuario en el backend...');
      
      // Configurar el token en headerService ANTES de hacer la petición
      headerService.setAuthToken(idToken);
      
      const userData: any = {
        full_name: firebaseUser.displayName || 'Usuario',
        picture: firebaseUser.photoURL || ''
      };

      // Agregar rol si está disponible
      if (role) {
        userData.role = role;
        console.log('📋 Rol especificado:', role);
      }

      console.log('📋 Payload del signup:', userData);

      const response = await httpClient.post(
        API_ENDPOINTS.USER_SIGN_UP,
        userData,
        'authenticated'
      );

      console.log('✅ Usuario creado/verificado en el backend:', response);
      return response;
      
    } catch (error: any) {
      console.error('❌ Error creando usuario en el backend:', error);
      throw error;
    }
  },

  /**
   * Refresca el token de Firebase
   */
  async refreshToken(): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('❌ No hay usuario autenticado para refrescar token');
        return false;
      }

      // Forzar refresh del token
      await currentUser.getIdToken(true);
      const newToken = await currentUser.getIdToken();
      
      // Obtener claims actualizados
      const tokenResult = await currentUser.getIdTokenResult();
      const customClaims = tokenResult.claims as any;
      
      // Actualizar token en headerService
      headerService.setAuthToken(newToken);
      
      // Actualizar storage con los nuevos claims si están disponibles
      if (customClaims.otsUser) {
        // Asegurar que el usuario tenga el id (uid de Firebase)
        const userDataWithId = {
          ...customClaims.otsUser,
          id: currentUser.uid
        };
        await storageService.saveUser(userDataWithId);
        console.log('💾 Storage actualizado con claims del backend');
      }
      
      console.log('✅ Token refrescado exitosamente');
      return true;
      
    } catch (error) {
      console.error('❌ Error refrescando token:', error);
      
      // Si falla el refresh, limpiar token
      headerService.clearAuthToken();
      await storageService.clearUser();
      
      return false;
    }
  },

  /**
   * Actualiza el rol del usuario autenticado en el backend y refresca sus claims
   */
  async upgradeCurrentUserRole(role: 'owner' | 'manager' | 'kitchen' | 'waiter'): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ No hay usuario autenticado para actualizar el rol');
        return false;
      }

      // Obtener token actual y llamar al endpoint de signup con el rol solicitado
      const idToken = await currentUser.getIdToken();
      await this.createUserInBackend(currentUser, idToken, role);

      // Refrescar token para traer nuevos claims
      await currentUser.getIdToken(true);
      const newToken = await currentUser.getIdToken();
      headerService.setAuthToken(newToken);

      // Actualizar storage con claims frescos
      const tokenResult = await currentUser.getIdTokenResult();
      const customClaims = tokenResult.claims as any;
      if (customClaims.otsUser) {
        const userDataWithId = {
          ...customClaims.otsUser,
          id: currentUser.uid
        };
        await storageService.saveUser(userDataWithId);
      }

      console.log('✅ Rol actualizado correctamente a', role);
      return true;
    } catch (error) {
      console.error('❌ Error actualizando rol del usuario:', error);
      return false;
    }
  },

  /**
   * Helper específico para elevar permisos a Owner
   */
  async upgradeCurrentUserToOwner(): Promise<boolean> {
    return this.upgradeCurrentUserRole('owner');
  },

  /**
   * Cierra sesión completamente
   */
  async signOut() {
    try {
      console.log('🔄 Cerrando sesión...');
      
      // 1. Cerrar sesión de Firebase
      await authService.signOut();
      
      // 2. Limpiar token de headers
      headerService.clearAuthToken();
      
      // 3. Limpiar datos de usuario del storage
      await storageService.clearUser();
      
      console.log('✅ Sesión cerrada completamente');
      
    } catch (error) {
      console.error('❌ Error cerrando sesión:', error);
      throw error;
    }
  }
}; 